import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import StoreSubnav from "@/components/store/StoreSubnav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  ShieldCheck,
  BookOpen,
  ShoppingCart,
  FileText,
  Star,
  User,
  Hash,
  Layers,
  Calendar,
  Tag,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useStoreCart, formatPrice } from "@/store/storeCart";
import { toast } from "sonner";

type Variant = {
  id: string;
  title: string | null;
  price_cents: number;
  stock_quantity: number | null;
  unlimited_stock: boolean;
  active: boolean;
};

type BookMeta = {
  author?: string;
  isbn?: string;
  pages?: number;
  publisher?: string;
  genre?: string;
  language?: string;
  publication_date?: string;
  edition?: string;
};

type Product = {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  base_price_cents: number;
  currency: string;
  images: string[] | null;
  product_type: "physical" | "digital";
  rating_avg: number | null;
  rating_count: number;
  metadata: BookMeta;
  vendor: {
    id: string;
    slug: string;
    display_name: string;
    identity_verified: boolean;
  };
  variants: Variant[];
};

const StoreBookDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const add = useStoreCart((s) => s.add);

  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [variantId, setVariantId] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from("store_products")
        .select(
          `id, slug, title, subtitle, description, base_price_cents, currency, images, product_type,
           rating_avg, rating_count, metadata,
           vendor:store_vendors_public!vendor_id(id, slug, display_name, identity_verified),
           variants:store_product_variants(id, title, price_cents, stock_quantity, unlimited_stock, active)`,
        )
        .eq("slug", slug)
        .eq("status", "active")
        .maybeSingle();
      if (cancelled) return;
      const p = data as unknown as Product | null;
      if (p && (p.vendor as any).status !== "active") {
        setProduct(null);
      } else {
        setProduct(p);
        const firstActive = p?.variants?.find((v) => v.active);
        setVariantId(firstActive?.id ?? null);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const selectedVariant = useMemo(
    () => product?.variants?.find((v) => v.id === variantId) ?? null,
    [product, variantId],
  );
  const unitPrice =
    selectedVariant?.price_cents ?? product?.base_price_cents ?? 0;
  const inStock = selectedVariant
    ? selectedVariant.unlimited_stock ||
      (selectedVariant.stock_quantity != null &&
        selectedVariant.stock_quantity > 0)
    : true;

  const meta = product?.metadata ?? {};

  const handleAdd = () => {
    if (!product) return;
    add({
      product_id: product.id,
      variant_id: variantId,
      vendor_id: product.vendor.id,
      vendor_slug: product.vendor.slug,
      vendor_name: product.vendor.display_name,
      product_slug: product.slug,
      title: product.title,
      variant_title: selectedVariant?.title ?? null,
      unit_price_cents: unitPrice,
      currency: product.currency,
      quantity: 1,
      image_url: product.images?.[0] ?? null,
      product_type: product.product_type,
      max_stock:
        selectedVariant && !selectedVariant.unlimited_stock
          ? selectedVariant.stock_quantity ?? null
          : null,
    });
    toast.success("Added to cart", {
      action: { label: "View cart", onClick: () => navigate("/store/cart") },
    });
  };

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <SiteHeader />
      <StoreSubnav />
      <main className="flex-1 mx-auto w-full max-w-6xl px-6 py-8">
        <Link
          to="/store/category/books"
          className="text-sm text-muted-foreground hover:underline"
        >
          ← Back to Books
        </Link>

        {loading ? (
          <div className="mt-6 grid md:grid-cols-[320px_1fr] gap-10">
            <Skeleton className="aspect-[2/3] rounded-lg" />
            <div className="space-y-3">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-6 w-1/3" />
              <Skeleton className="h-24 w-full" />
            </div>
          </div>
        ) : !product ? (
          <Card className="p-12 text-center mt-6">
            <h1 className="text-xl font-semibold">Book not found</h1>
            <p className="text-sm text-muted-foreground mt-2">
              It may have been removed or is no longer available.
            </p>
          </Card>
        ) : (
          <div className="mt-6 grid md:grid-cols-[320px_1fr] gap-10">
            {/* Book cover */}
            <div className="flex flex-col items-center gap-4">
              <div className="aspect-[2/3] w-full max-w-[320px] rounded-lg overflow-hidden bg-muted/50 flex items-center justify-center shadow-lg">
                {product.images?.[0] ? (
                  <img
                    src={product.images[0]}
                    alt={product.title}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <BookOpen className="h-20 w-20 text-muted-foreground/30" />
                )}
              </div>

              {/* Thumbnail strip for additional images */}
              {product.images && product.images.length > 1 && (
                <div className="grid grid-cols-4 gap-2 w-full max-w-[320px]">
                  {product.images.slice(1).map((url, i) => (
                    <div
                      key={i}
                      className="aspect-square rounded overflow-hidden border border-border"
                    >
                      <img
                        src={url}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Book details */}
            <div className="space-y-5">
              {/* Author & publisher line */}
              {meta.author && (
                <p className="text-base text-muted-foreground font-medium">
                  by {meta.author}
                </p>
              )}

              <h1 className="text-3xl md:text-4xl font-bold leading-tight">
                {product.title}
              </h1>

              {product.subtitle && (
                <p className="text-lg text-muted-foreground">
                  {product.subtitle}
                </p>
              )}

              {/* Rating */}
              {product.rating_count > 0 && product.rating_avg != null && (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        className={`h-4 w-4 ${
                          i < Math.round(product.rating_avg!)
                            ? "text-yellow-500 fill-yellow-500"
                            : "text-muted-foreground/30"
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-sm text-muted-foreground">
                    ({product.rating_count})
                  </span>
                </div>
              )}

              {/* Price + badges */}
              <div className="flex items-center gap-4 flex-wrap">
                <p className="text-2xl font-semibold">
                  {formatPrice(unitPrice, product.currency)}
                </p>
                <Badge variant="secondary">
                  {product.product_type === "digital"
                    ? "eBook / Digital"
                    : "Paperback / Hardcover"}
                </Badge>
                {meta.genre && (
                  <Badge variant="outline" className="gap-1">
                    <Tag className="h-3 w-3" />
                    {meta.genre}
                  </Badge>
                )}
              </div>

              {/* Vendor */}
              <Link
                to={`/store/vendor/${product.vendor.slug}`}
                className="text-sm text-muted-foreground hover:underline inline-flex items-center gap-1"
              >
                {product.vendor.display_name}
                {product.vendor.identity_verified && (
                  <ShieldCheck className="h-4 w-4 text-primary" />
                )}
              </Link>

              {/* Format variants (e.g. Paperback, Hardcover, eBook) */}
              {product.variants?.filter((v) => v.active).length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Format</p>
                  <div className="flex flex-wrap gap-2">
                    {product.variants
                      .filter((v) => v.active)
                      .map((v) => (
                        <button
                          key={v.id}
                          onClick={() => setVariantId(v.id)}
                          className={`px-4 py-2 rounded-md border text-sm transition-colors ${
                            variantId === v.id
                              ? "border-primary bg-primary/10 font-medium"
                              : "border-border hover:border-foreground/40"
                          }`}
                        >
                          {v.title ?? "Standard"}
                          <span className="ml-2 text-xs text-muted-foreground">
                            {formatPrice(v.price_cents, product.currency)}
                          </span>
                        </button>
                      ))}
                  </div>
                </div>
              )}

              {/* CTA */}
              <div className="flex gap-3 pt-2">
                <Button
                  size="lg"
                  onClick={handleAdd}
                  disabled={!inStock}
                  className="flex-1 max-w-xs"
                >
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  {inStock ? "Add to cart" : "Out of stock"}
                </Button>
              </div>

              <Separator />

              {/* Book metadata table */}
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  Book details
                </h3>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  {meta.author && (
                    <>
                      <span className="text-muted-foreground inline-flex items-center gap-1.5">
                        <User className="h-3.5 w-3.5" /> Author
                      </span>
                      <span>{meta.author}</span>
                    </>
                  )}
                  {meta.publisher && (
                    <>
                      <span className="text-muted-foreground inline-flex items-center gap-1.5">
                        <Layers className="h-3.5 w-3.5" /> Publisher
                      </span>
                      <span>{meta.publisher}</span>
                    </>
                  )}
                  {meta.pages && (
                    <>
                      <span className="text-muted-foreground inline-flex items-center gap-1.5">
                        <FileText className="h-3.5 w-3.5" /> Pages
                      </span>
                      <span>{meta.pages}</span>
                    </>
                  )}
                  {meta.isbn && (
                    <>
                      <span className="text-muted-foreground inline-flex items-center gap-1.5">
                        <Hash className="h-3.5 w-3.5" /> ISBN
                      </span>
                      <span className="font-mono text-xs">{meta.isbn}</span>
                    </>
                  )}
                  {meta.language && (
                    <>
                      <span className="text-muted-foreground inline-flex items-center gap-1.5">
                        <BookOpen className="h-3.5 w-3.5" /> Language
                      </span>
                      <span>{meta.language}</span>
                    </>
                  )}
                  {meta.publication_date && (
                    <>
                      <span className="text-muted-foreground inline-flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5" /> Published
                      </span>
                      <span>{meta.publication_date}</span>
                    </>
                  )}
                  {meta.edition && (
                    <>
                      <span className="text-muted-foreground inline-flex items-center gap-1.5">
                        <BookOpen className="h-3.5 w-3.5" /> Edition
                      </span>
                      <span>{meta.edition}</span>
                    </>
                  )}
                </div>
              </div>

              {/* Description / Synopsis */}
              {product.description && (
                <>
                  <Separator />
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                      Synopsis
                    </h3>
                    <p className="whitespace-pre-wrap">{product.description}</p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
};

export default StoreBookDetailPage;
